import { fmtCurrency, fmtInt } from '../lib/format.ts';

export interface StackedSegment {
  label: string;
  value: number;
  /** classe tailwind ex: 'bg-[--color-ok]' */
  colorClass: string;
}

interface StackedBarProps {
  title: string;
  segments: StackedSegment[];
  /** se 'currency', segmentos exibem R$; senao inteiro */
  format?: 'int' | 'currency';
  /** total exibido a direita (default = soma) */
  totalOverride?: number;
  /** legenda com porcentagem */
  showPercent?: boolean;
}

/**
 * StackedBar = barra horizontal segmentada com legenda inline.
 * Usado para inadimplencia por idade (ate30/31-60/61-90/90+).
 */
export function StackedBar({ title, segments, format = 'int', totalOverride, showPercent = true }: StackedBarProps) {
  const total = totalOverride ?? segments.reduce((acc, s) => acc + s.value, 0);
  const safeTotal = total > 0 ? total : 1;
  const fmt = (n: number) => (format === 'currency' ? fmtCurrency(n) : fmtInt(n));

  return (
    <div className="rounded-2xl border border-[--color-border] bg-[--color-card] p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[--color-fg-muted]">
          {title}
        </h3>
        <span className="text-sm tabular-nums text-[--color-fg]">total {fmt(total)}</span>
      </div>

      <div className="mt-4 flex h-4 w-full overflow-hidden rounded-full bg-[--color-card-hi]">
        {segments.map((s) => {
          const pct = (s.value / safeTotal) * 100;
          if (pct <= 0) return null;
          return (
            <div
              key={s.label}
              className={`h-full ${s.colorClass} transition-[width] duration-500`}
              style={{ width: `${pct}%` }}
              title={`${s.label}: ${fmt(s.value)} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
        {segments.map((s) => {
          const pct = (s.value / safeTotal) * 100;
          return (
            <div key={s.label} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className={`inline-block h-2.5 w-2.5 rounded-sm ${s.colorClass}`} />
                <span className="text-[10px] uppercase tracking-wider text-[--color-fg-muted]">{s.label}</span>
              </div>
              <div className="flex items-baseline gap-2 tabular-nums">
                <span className="text-lg font-semibold text-[--color-fg]">{fmt(s.value)}</span>
                {showPercent && <span className="text-[10px] text-[--color-fg-dim]">{pct.toFixed(0)}%</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
